import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bookmark, AlertTriangle } from 'lucide-react';
import { slotStatsApi } from '../../lib/api';
import { classifyR14Error, r14ErrorText } from '../../lib/r14Errors';
import { normaliseSavedCollection, splitCollectionReport, anyTotal } from '../../lib/savedCollectionView';
import { fmtInt, fmtServerDateTime } from '../../lib/slotStatsView';
import {
  StateChip, Figure, CompactLine, logHidden, logFailed, NOT_RECORDED,
} from './R15Parts';

/*
 * Round 15 (2026-09-30), Varun item 7: the saved-collection card of the Slot Stats page (lib/savedCollectionView.js
 * has the server contract).
 *
 *   - GET /uiapi/slots/{slot}/saved-collection answers 404 / 405 (a server before round 15) or 404 not_enabled
 *     (the part is switched off on the server - the default until the live run passes): NOTHING is drawn.
 *   - 697 not ticked on this slot: one compact line (with the totals recorded before, if any).
 *   - Otherwise: "Saved collection '<698>'", the READ / QUEUED / REPOSTED / FAILED totals (and "removed" only when
 *     consume mode is on for the slot or a removal was recorded), when the collection was last read, the last
 *     report with its time - the worker's reasons listed as it wrote them - and what stops the step as far as the
 *     server can see. The server keeps totals only, so there is no day-by-day chart.
 * Row labels are V1's (slot-stats.html, round 15c). Every missing total reads "not recorded", never 0.
 */

const val = (v) => (v === null || v === undefined ? null : fmtInt(v));

/**
 * The collection name in ONE pair of curly quotes: ‘Summer picks’. Round 15b (2026-10-01): the title's font (Space
 * Grotesk) draws a straight ' as a closing quote, so 'Summer picks' read as ’Summer picks’ (two closing marks).
 * Quotes the name already carries at both ends (a 698 saved as 'Summer picks') are not doubled.
 */
export function quotedName(n) {
  const s = String(n || '').trim().replace(/^(['"‘’“”])([\s\S]*)\1$|^[‘“]([\s\S]*)[’”]$/, (m, q, a, b) => (a !== undefined ? a : b)).trim();
  return s ? `‘${s}’` : '(no name)';
}

export default function SavedCollectionCard({ slotName }) {
  const q = useQuery({
    queryKey: ['savedCollection', slotName],
    queryFn: () => slotStatsApi.savedCollection(slotName).then((r) => r.data),
    retry: false,
  });
  if (q.isLoading) return null;
  if (q.error) {
    const c = classifyR14Error(q.error);
    if (c.kind === 'routeMissing' || c.kind === 'notEnabled') {
      logHidden('saved-collection', slotName, c.kind === 'notEnabled'
        ? `switched off on this server (${c.message || 'not_enabled'})` : `no /saved-collection route on this server (HTTP ${c.status})`);
      return null;
    }
    logFailed('saved-collection', slotName, c);
    return (
      <CompactLine icon={Bookmark} title="Saved collection" testid="saved-collection-error" tone="error">
        {c.kind === 'slotNotFound'
          ? (c.message || `No slot ${slotName} on this account.`)
          : `The figures could not be loaded (${r14ErrorText(q.error, 'no answer')}).`}
      </CompactLine>
    );
  }
  const sc = normaliseSavedCollection(q.data);
  if (!sc) {
    logHidden('saved-collection', slotName, 'the answer carries no saved-collection fields');
    return null;
  }
  const name = quotedName(sc.collectionName);
  const t = sc.totals;
  if (!sc.enabled) {
    return (
      <CompactLine icon={Bookmark} title={`Saved collection ${name}`} testid="saved-collection-off">
        Off (697 &quot;Repost Posts From My Saved Collection&quot; is not ticked)
        {anyTotal(sc)
          ? ` · so far: read ${orNr(t.read)} · queued ${orNr(t.queued)} · reposted ${orNr(t.posted)} · failed ${orNr(t.failed)}`
            + `${sc.showRemoved ? ` · removed ${orNr(t.removed)}` : ''}`
          : ''}
      </CompactLine>
    );
  }
  const report = splitCollectionReport(sc.lastReport);
  const perSession = sc.linksPerSession === null ? null
    : `${fmtInt(sc.linksPerSession)}${sc.linksPerSessionSaved === null ? ' (default)' : ''}`
      + `${sc.linksPerSessionSaved !== null && sc.linksPerSessionSaved !== sc.linksPerSession
        ? ` (saved ${fmtInt(sc.linksPerSessionSaved)}, the worker uses 1 to 10)` : ''}`;

  return (
    <section className="aurora-card min-w-0 p-4 sm:p-5" data-testid="saved-collection-card" aria-label="Saved collection">
      <header className="mb-3">
        <h2 className="flex flex-wrap items-center gap-2 font-heading text-base font-semibold leading-tight text-foreground">
          <Bookmark className="h-4 w-4 shrink-0 text-primary" aria-hidden />
          <span data-testid="saved-collection-title">Saved collection {name}</span>
          <StateChip label={sc.blockers.length ? 'Stopped' : 'On'} tone={sc.blockers.length ? 'warn' : 'ok'}
            code={sc.blockers.length ? 'STOPPED' : 'ON'} testid="saved-collection-state" />
        </h2>
        <p className="mt-1 text-xs text-muted-foreground text-pretty" data-testid="saved-collection-settings">
          697 &quot;Repost Posts From My Saved Collection&quot;: ON
          {perSession !== null && <> · 699 posts to read per session: {perSession}</>}
          {sc.consumeAvailable && (
            <span data-testid="saved-collection-700">
              {' '}· 700 Remove from this collection once queued: {sc.removeAfterQueued
                ? 'ON (removed from this collection only; the post stays saved)'
                : 'OFF (the collection is never changed)'}
            </span>
          )}
        </p>
      </header>

      <div className={`grid grid-cols-2 gap-2.5 ${sc.showRemoved ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`} data-testid="saved-collection-totals">
        <Figure label="Read" testid="saved-collection-read" value={val(t.read)} caption="Links copied on the phone" />
        <Figure label="Queued" testid="saved-collection-queued" value={val(t.queued)} caption="Used: folder, caption and history verified" />
        <Figure label="Reposted" testid="saved-collection-posted" value={val(t.posted)} />
        <Figure label="Failed" testid="saved-collection-failed" value={val(t.failed)} />
        {sc.showRemoved && (
          <Figure label="Removed" testid="saved-collection-removed" value={val(t.removed)}
            caption="From this collection (consume mode; still saved)" />
        )}
      </div>

      <dl className="mt-4 divide-y divide-white/5 border-t border-white/5">
        <div className="grid gap-x-4 gap-y-0.5 py-2 sm:grid-cols-[13rem_minmax(0,1fr)]" data-testid="saved-collection-last-read">
          <dt className="text-xs text-muted-foreground">Last read</dt>
          <dd className="text-sm text-foreground">{sc.lastReadAt ? fmtServerDateTime(sc.lastReadAt) : <span className="text-muted-foreground">{NOT_RECORDED}</span>}</dd>
        </div>
        <div className="grid gap-x-4 gap-y-0.5 py-2 sm:grid-cols-[13rem_minmax(0,1fr)]" data-testid="saved-collection-report">
          <dt className="text-xs text-muted-foreground">Last report</dt>
          <dd className="min-w-0 text-sm text-foreground">
            {report ? (
              <div className="space-y-1.5">
                {report.at && (
                  <p className="text-muted-foreground tnum" data-testid="saved-collection-report-time">
                    {fmtServerDateTime(report.at)}{report.zone ? ` (${report.zone})` : ''}
                  </p>
                )}
                {report.counts && <p className="break-words font-mono text-xs" data-testid="saved-collection-report-counts">{report.counts}</p>}
                {report.notes.length > 0 && (
                  <ul className="list-disc space-y-0.5 pl-4 text-xs text-foreground/90" data-testid="saved-collection-report-notes">
                    {report.notes.map((n, i) => (
                      <li key={`${i}-${n}`} className="break-words" data-testid="saved-collection-report-note">{n}</li>
                    ))}
                  </ul>
                )}
              </div>
            ) : <span className="text-muted-foreground">{NOT_RECORDED}</span>}
          </dd>
        </div>
      </dl>

      {sc.blockers.length > 0 && (
        <div className="mt-3 rounded-xl border border-[hsl(var(--warning)/.35)] bg-[hsl(var(--warning)/.08)] px-3.5 py-3" data-testid="saved-collection-blockers">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--warning))]">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden /> What stops it
          </p>
          <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-sm text-foreground">
            {sc.blockers.map((b) => <li key={b} data-testid="saved-collection-blocker">{b}</li>)}
          </ul>
        </div>
      )}
    </section>
  );
}

function orNr(v) {
  return v === null || v === undefined ? NOT_RECORDED : fmtInt(v);
}
